import React, { useState, useEffect } from 'react';
import { Copy, ExternalLink, Wallet, ArrowDown, ArrowUp, History, RefreshCw, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@vaiu/ui/card';
import { Button } from '@vaiu/ui/button';
import { Badge } from '@vaiu/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@vaiu/ui/tabs';
import { Alert, AlertDescription } from '@vaiu/ui/alert';
import { useToast } from '@/hooks/use-toast';
import { useEthereum } from '@/context/EthereumContext';
import { formatDistanceToNow } from 'date-fns';
import EthereumWalletCreation from './EthereumWalletCreation';
import EthereumDepositFlow from './EthereumDepositFlow';
import EthereumWithdrawFlowEnhanced from './EthereumWithdrawFlowEnhanced';
import EthereumTransactionHistory from './EthereumTransactionHistory';

const EthereumWalletDashboard: React.FC = () => {
  const { toast } = useToast();
  const {
    wallet,
    balance,
    serviceStatus,
    loading,
    error,
    refreshBalance,
    checkServiceStatus,
  } = useEthereum();

  const [activeTab, setActiveTab] = useState('overview');
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    // Refresh balance every 30 seconds
    const interval = setInterval(() => {
      refreshBalance();
    }, 30000);

    return () => clearInterval(interval);
  }, []);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await Promise.all([
        refreshBalance(),
        checkServiceStatus(),
      ]);
      toast({
        title: 'Refreshed',
        description: 'Wallet data has been updated',
      });
    } catch (error) {
      console.error('Refresh failed:', error);
    } finally {
      setRefreshing(false);
    }
  };

  const copyAddress = () => {
    if (wallet?.address) {
      navigator.clipboard.writeText(wallet.address);
      toast({
        title: 'Copied',
        description: 'Wallet address copied to clipboard',
      });
    }
  };

  const formatAddress = (address: string) => {
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  };

  const getNetworkBadge = () => {
    if (!wallet) return null;
    
    const isMainnet = wallet.network === 'mainnet';
    return (
      <Badge variant={isMainnet ? 'default' : 'secondary'}>
        {isMainnet ? '🟢' : '🟡'} {wallet.network}
      </Badge>
    );
  };

  if (loading && !wallet) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (!serviceStatus?.enabled) {
    return (
      <Alert>
        <AlertDescription>
          Ethereum funding is not currently available. Please check back later.
        </AlertDescription>
      </Alert>
    );
  }

  if (!wallet) {
    return <EthereumWalletCreation />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Ethereum Wallet</h2>
          <p className="text-muted-foreground">
            Manage your ETH deposits and withdrawals
          </p>
        </div>
        <div className="flex items-center space-x-2">
          {getNetworkBadge()}
          <Button
            variant="outline"
            size="sm"
            onClick={handleRefresh}
            disabled={refreshing}
          >
            {refreshing ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4" />
            )}
          </Button>
        </div>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Wallet Card */}
      <Card>
        <CardHeader>
          <CardTitle>Wallet Details</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Address */}
          <div>
            <label className="text-sm text-muted-foreground">Address</label>
            <div className="flex items-center space-x-2 mt-1">
              <code className="flex-1 p-2 bg-muted rounded text-sm">
                {wallet.address}
              </code>
              <Button
                variant="ghost"
                size="sm"
                onClick={copyAddress}
              >
                <Copy className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                asChild
              >
                <a
                  href={`https://etherscan.io/address/${wallet.address}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <ExternalLink className="h-4 w-4" />
                </a>
              </Button>
            </div>
          </div>

          {/* Balance */}
          <div className="grid grid-cols-2 gap-4">
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">ETH Balance</p>
                    <p className="text-2xl font-bold">
                      {balance?.balanceEth?.toFixed(6) || '0.000000'}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      ≈ ${balance?.balanceUsd?.toFixed(2) || '0.00'} USD
                    </p>
                  </div>
                  <Wallet className="h-8 w-8 text-muted-foreground" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">ETH Price</p>
                    <p className="text-2xl font-bold">
                      ${balance?.ethPrice?.toFixed(2) || serviceStatus?.ethPrice?.toFixed(2) || '0.00'}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      Current market price
                    </p>
                  </div>
                  <div className="text-2xl">📈</div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Gas Prices */}
          {serviceStatus?.gasPrice && (
            <div>
              <label className="text-sm text-muted-foreground">Network Gas Prices</label>
              <div className="grid grid-cols-3 gap-2 mt-1">
                <Badge variant="secondary">
                  🐢 Low: {(parseInt(serviceStatus.gasPrice.low) / 1e9).toFixed(0)} Gwei
                </Badge>
                <Badge variant="default">
                  🚶 Med: {(parseInt(serviceStatus.gasPrice.medium) / 1e9).toFixed(0)} Gwei
                </Badge>
                <Badge variant="destructive">
                  🏃 High: {(parseInt(serviceStatus.gasPrice.high) / 1e9).toFixed(0)} Gwei
                </Badge>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Actions Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="deposit">
            <ArrowDown className="h-4 w-4 mr-2" />
            Deposit
          </TabsTrigger>
          <TabsTrigger value="withdraw">
            <ArrowUp className="h-4 w-4 mr-2" />
            Withdraw
          </TabsTrigger>
          <TabsTrigger value="history">
            <History className="h-4 w-4 mr-2" />
            History
          </TabsTrigger>
        </TabsList>

        <TabsContent value="deposit" className="mt-4">
          <EthereumDepositFlow />
        </TabsContent>

        <TabsContent value="withdraw" className="mt-4">
          <EthereumWithdrawFlowEnhanced balance={balance} />
        </TabsContent>

        <TabsContent value="history" className="mt-4">
          <EthereumTransactionHistory />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default EthereumWalletDashboard;
